/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client'

import {
  onSnapshot,
  type DocumentData,
  type DocumentReference,
  type FirestoreError,
} from 'firebase/firestore'
import { useEffect, useRef, useState, type DependencyList } from 'react'
import { CONFIRMABLE_LISTEN_OPTIONS } from './helpers/listen-options'
import {
  DENIAL_STREAK_TO_REPORT,
  denialLabelForQuery,
  reportFirestoreDenial,
  reportFirestoreServerRead,
  scheduleRefusedReopen,
  subscribeFirestoreSessionHeal,
} from './firestore-denial-reporter'
import { armFirestoreStallWatch } from './firebase/firestore-stall-recovery'

const RETRY_DELAY_MS = 400
const MAX_RETRIES = 5
// The refused cadence is shared policy — see `scheduleRefusedReopen` in
// `firestore-denial-reporter.ts` (AGL-1066, AGL-1440, AGL-2944, AGL-2945).

export type FirestoreDocStatus = 'loading' | 'success' | 'error'

export interface UseFirestoreDocOptions {
  idField?: string
}

export interface UseFirestoreDocResult<T> {
  data: T | undefined
  status: FirestoreDocStatus
  error: FirestoreError | undefined
  /**
   * `data` still carries writes this client made that the server has NOT
   * acknowledged. Carried rather than dropped for the same reason as
   * `ObservableStatus.hasPendingWrites` — see that comment (AGL-1262).
   */
  hasPendingWrites: boolean
  /**
   * `data` came from the local cache and the server has not confirmed it
   * (AGL-1066). See `ObservableStatus.fromCache` for why this matters and
   * why it is the signal to reach for rather than `staleSession`.
   */
  fromCache: boolean
  /**
   * The server has REFUSED this listen for longer than the retry budget and
   * no server snapshot has arrived since (AGL-1066) — the reason behind an
   * `error`, not a duplicate of it. See
   * `UseFirestoreCollectionResult.serverDenied`.
   */
  serverDenied: boolean
}

/**
 * Single-document counterpart to `useFirestoreCollection` — see that hook's
 * doc comment for why this exists instead of reactfire's
 * `useFirestoreDocData` (AGL-216/217).
 *
 * `buildRef` is re-invoked from `deps` the same way a `useEffect`/`useMemo`
 * callback would be — pass the same primitive values (ids, uid, firestore)
 * you'd put in a `useMemo` dependency array, not the `DocumentReference`
 * object itself.
 */
export function useFirestoreDoc<T = DocumentData>(
  buildRef: () => DocumentReference<DocumentData> | null | undefined,
  deps: DependencyList,
  options: UseFirestoreDocOptions = {},
): UseFirestoreDocResult<T> {
  const [data, setData] = useState<T | undefined>(undefined)
  const [status, setStatus] = useState<FirestoreDocStatus>('loading')
  const [error, setError] = useState<FirestoreError | undefined>(undefined)
  const [hasPendingWrites, setHasPendingWrites] = useState(false)
  // Un-confirmed until a snapshot says otherwise: a hook that has not heard
  // from the server yet must not read as server-confirmed.
  const [fromCache, setFromCache] = useState(true)
  const [serverDenied, setServerDenied] = useState(false)
  const buildRefRef = useRef(buildRef)
  buildRefRef.current = buildRef
  const idField = options.idField

  useEffect(() => {
    setStatus('loading')
    setError(undefined)
    // Clear on every dep change (AGL-591): a scope move between two live
    // refs must not keep showing the previous doc until the new snapshot
    // arrives. Mirrors use-firestore-collection.
    setData(undefined)
    setHasPendingWrites(false)
    setFromCache(true)
    setServerDenied(false)

    const ref = buildRefRef.current()
    if (!ref) {
      return
    }

    let cancelled = false
    let unsubscribe: (() => void) | null = null
    let timer: ReturnType<typeof setTimeout> | null = null
    /** Cancels a pending reopen on the refused cadence (AGL-2944). */
    let cancelRefusedReopen: (() => void) | null = null
    let attempt = 0
    // Refusals since the last SERVER snapshot (AGL-1066) — see the note on
    // `DENIAL_STREAK_TO_REPORT` for why this is not `attempt`.
    let deniedStreak = 0
    /** Epoch ms of the current streak's first refusal (AGL-1440). */
    let deniedStreakStartedAt = 0
    let denialReported = false
    /**
     * The retry budget is spent and the server's last word was a refusal —
     * what keeps `status: 'error'` from being undone by the cached emission
     * that precedes every reopened refusal (AGL-1066). See the longer note
     * on the same variable in `use-firestore-collection`.
     */
    let terminal = false
    const denialLabel = denialLabelForQuery(ref)
    /**
     * A listen the server has not confirmed within `FIRESTORE_STALL_MS`,
     * while the browser is online and the tab is visible, is the multi-tab
     * wedge (AGL-3373): no tab is syncing, so an empty cache raises no first
     * snapshot at all and a warm one never confirms. The client is asked to
     * recover — a network cycle that forces the lease election — and this
     * listener then hears from the server like any other. Disarmed by the
     * first server-confirmed snapshot, by an error (a refusal is not a
     * stall) and by cleanup.
     */
    const disarmStall = armFirestoreStallWatch(ref.firestore)

    const subscribe = () => {
      unsubscribe = onSnapshot(
        ref,
        // Without this the cache→server confirmation is never delivered and
        // `fromCache` latches true for the life of the listener, refusing
        // every guarded save on the page (AGL-2486). See `listen-options.ts`.
        CONFIRMABLE_LISTEN_OPTIONS,
        (snapshot) => {
          if (cancelled) return
          if (!terminal) {
            setStatus('success')
            setError(undefined)
          }
          setHasPendingWrites(snapshot.metadata.hasPendingWrites)
          setFromCache(snapshot.metadata.fromCache)
          // Only the SERVER answering is evidence the session can read — and
          // the only thing that refunds the retry budget (AGL-1066). See the
          // long note on the same gate in `use-firestore-collection`.
          if (!snapshot.metadata.fromCache) {
            disarmStall()
            attempt = 0
            terminal = false
            setStatus('success')
            setError(undefined)
            deniedStreak = 0
            denialReported = false
            setServerDenied(false)
            // Proof the session can read — stronger than any accumulated
            // denial, and what stops a scoped collaborator's by-design
            // denials from ever adding up to a re-auth prompt.
            reportFirestoreServerRead()
          } else if (deniedStreak === 0) {
            // Cached, but nothing has been refused since the server last
            // answered — not the AGL-1066 fault, so the budget is refunded
            // exactly as it always was. This is what keeps offline inert:
            // `unavailable` never enters `deniedStreak`.
            attempt = 0
          }
          if (!snapshot.exists()) {
            setData(undefined)
            return
          }
          const value = { ...snapshot.data() } as Record<string, unknown>
          if (idField) value[idField] = snapshot.id
          setData(value as T)
        },
        (err) => {
          if (cancelled) return
          disarmStall()
          unsubscribe?.()
          // Say so once per outage, and only for a refusal — a lost network
          // produces no error callback at all, so this cannot fire offline.
          if (err?.code === 'permission-denied') {
            deniedStreak += 1
            if (deniedStreak === 1) deniedStreakStartedAt = Date.now()
            if (deniedStreak >= DENIAL_STREAK_TO_REPORT) {
              setServerDenied(true)
              if (!denialReported) {
                denialReported = true
                reportFirestoreDenial(denialLabel)
              }
            }
          }
          if (attempt < MAX_RETRIES) {
            attempt += 1
            if (deniedStreak > MAX_RETRIES) {
              cancelRefusedReopen = scheduleRefusedReopen(
                subscribe,
                deniedStreakStartedAt,
              )
            } else {
              timer = setTimeout(subscribe, RETRY_DELAY_MS)
            }
          } else {
            terminal = true
            setStatus('error')
            setError(err)
            // A refusal streak keeps a slow road back for a recovery nobody
            // announced — see the same branch in `use-firestore-collection`.
            // The cadence splits on session-vs-ref evidence (AGL-1440), grows
            // with the streak's age (AGL-2945) and waits while the tab is
            // hidden (AGL-2944).
            if (deniedStreak > MAX_RETRIES) {
              cancelRefusedReopen = scheduleRefusedReopen(
                subscribe,
                deniedStreakStartedAt,
              )
            }
          }
        },
      )
    }
    subscribe()

    // Reopen when the session heals — see the long note on the same
    // subscription in `use-firestore-collection` (AGL-1066). Gated on this
    // listen actually being refused, so a healthy one ignores the broadcast.
    const unsubscribeHeal = subscribeFirestoreSessionHeal(() => {
      if (cancelled || deniedStreak === 0) return
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      cancelRefusedReopen?.()
      cancelRefusedReopen = null
      unsubscribe?.()
      attempt = 0
      denialReported = false
      subscribe()
    })

    return () => {
      cancelled = true
      disarmStall()
      unsubscribeHeal()
      if (timer) clearTimeout(timer)
      cancelRefusedReopen?.()
      unsubscribe?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return { data, status, error, hasPendingWrites, fromCache, serverDenied }
}

export default useFirestoreDoc
